import { Button } from "@/Components/ui/button";
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
   DialogTrigger,
} from "@/Components/ui/dialog";
import { Input } from "@/Components/ui/input";
import { ScrollArea } from "@/Components/ui/scroll-area";
import {
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableHeader,
   TableRow,
} from "@/Components/ui/table";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/Components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/Components/ui/tabs";

import type { MainProduct } from "@/types/productTypes";
import { Search, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useAdminStore } from "../../../../store/useAdminStore";
import fetchDb from "../../../../utils/customRequestFunction";

// Types for our local dependencies
interface LocalDependencyEntry {
   itemId: number;
   itemName: string;
   priceAdjustment: number;
   isItemDependency: boolean;
   affectingOptionId: number;
   nameChange: string;
   learnMoreId?: number;
}

export function getItemInefficient(
   allProducts: MainProduct[],
   itemId: number | undefined
) {
   if (!itemId) return null;
   for (const product of allProducts) {
      for (const stage of product.stages ?? []) {
         for (const option of stage.options ?? []) {
            // see if this option has the target item
            const foundItem = (option.items ?? []).find(
               (item) => item.id === itemId
            );
            if (foundItem) {
               return foundItem;
            }
         }
      }
   }
   return null; // none found
}

export default function DependencyDialog() {
   const {
      selectedOptionId,
      selectedStageId,
      selectedItemId,
      selectedProductId,
      fetchItemDependencies,
      ...adminStore
   } = useAdminStore();
   useEffect(() => {
      if (
         selectedProductId &&
         selectedStageId &&
         selectedOptionId &&
         selectedItemId
      ) {
         fetchItemDependencies(
            selectedProductId,
            selectedStageId,
            selectedOptionId,
            selectedItemId
         );
      }
   }, [selectedProductId, selectedStageId, selectedOptionId, selectedItemId]);
   // Control the entire dialog open/close
   const [isDialogOpen, setIsDialogOpen] = useState(false);

   // Which option is selected in the left column
   const [selectedSidebarOptionId, setSelectedSidebarOptionId] = useState<
      number | null | undefined
   >(null);

   // The "cart" or "selected dependencies" on the right
   const [addedDependencies, setAddedDependencies] = useState<
      LocalDependencyEntry[]
   >([]);
   // Simple search/filter for items in the center column
   const [itemSearch, setItemSearch] = useState("");

   // Add state for available LearnMore items
   const [availableLearnMoreItems, setAvailableLearnMoreItems] = useState<
      { id: number; content: string }[]
   >([]);

   // Fetch available LearnMore items when the dialog opens
   useEffect(() => {
      if (isDialogOpen) {
         fetchLearnMoreItems();
      }
   }, [isDialogOpen]);

   const fetchLearnMoreItems = async () => {
      try {
         const learnMoreItems = await fetchDb.get("/api/learn-more");
         setAvailableLearnMoreItems(learnMoreItems || []);
      } catch (error) {
         console.error("Error fetching LearnMore items:", error);
      }
   };

   /**
    * Calculate and set the correct dependencies based on the selected item.
    */
   const calculateCorrectDeps = () => {
      if (
         !selectedProductId ||
         !selectedStageId ||
         !selectedOptionId ||
         !selectedItemId
      )
         return;

      console.log(
         "selected",
         selectedProductId,
         selectedStageId,
         selectedOptionId,
         selectedItemId,
         adminStore.allProducts
      );

      const item = getItemInefficient(adminStore.allProducts, selectedItemId);
      console.log("item", item);
      if (!item) return;

      if (
         item.dependentDependencyManager &&
         item.dependentDependencyManager.length > 0
      ) {
         console.log("item", item);
         console.log("depenManager", item.dependentDependencyManager);

         const separatedManager = item.dependentDependencyManager
            .map((manager) => {
               const affectingItem = getItemInefficient(
                  adminStore.allProducts,
                  manager.affectingItemId
               );
               if (!affectingItem) return null;

               if (!manager.affectingItemId || !affectingItem) {
                  return null; // Exclude items with undefined values
               }

               return {
                  itemId: manager.affectingItemId,
                  itemName: affectingItem.name, // Guaranteed to be defined
                  priceAdjustment:
                     manager.priceDependency?.priceAdjustment ?? 0, // Default to 0 if undefined
                  isItemDependency: !!manager.itemDependency, // Convert to boolean
                  affectingOptionId: manager.affectingOptionId,
                  nameChange: manager.nameDependency?.nameChange ?? "", // Include nameChange
                  learnMoreId: manager.learnMoreDependency?.learnMoreId, // Include learnMoreId
               };
            })
            .filter((dep) => dep !== null) as LocalDependencyEntry[]; // Type assertion

         setAddedDependencies(separatedManager);
      } else {
         setAddedDependencies([]);
      }
   };

   useEffect(() => {
      if (isDialogOpen) {
         calculateCorrectDeps();
         console.log("recalculating");
      }
   }, [selectedItemId, isDialogOpen]);

   useEffect(() => {
      console.log("addedDeps", addedDependencies);
   }, [addedDependencies]);

   // ----- Handlers -----

   const handleOpenDialog = () => {
      setIsDialogOpen(true);
      // Clear any previous selections
      calculateCorrectDeps();
      setSelectedSidebarOptionId(null);
      setItemSearch("");
   };

   /**
    * The user clicks an Option on the left to see its items in the center.
    */

   /**
    * "Add" an item to the right-side dependencies table.
    * If it's already added, we won't add a duplicate.
    */
   const handleAddItem = (
      itemId: number | undefined,
      itemName: string,
      affectingOptionId: number | undefined
   ) => {
      if (!itemId || !affectingOptionId) return;
      setAddedDependencies((prev) => {
         const existing = prev.find((x) => x.itemId === itemId);
         if (existing) return prev;
         return [
            ...prev,
            {
               itemId,
               itemName,
               priceAdjustment: 0,
               isItemDependency: false,
               affectingOptionId,
               nameChange: "",
            },
         ];
      });
   };

   /**
    * User modifies "priceAdjustment" in the right-side table
    */
   const handleChangePrice = (itemId: number, newPrice: number) => {
      setAddedDependencies((prev) =>
         prev.map((dep) =>
            dep.itemId === itemId ? { ...dep, priceAdjustment: newPrice } : dep
         )
      );
   };

   /**
    * User modifies "nameChange" in the right-side table
    */
   const handleChangeName = (itemId: number, newName: string) => {
      setAddedDependencies((prev) =>
         prev.map((dep) =>
            dep.itemId === itemId ? { ...dep, nameChange: newName } : dep
         )
      );
   };

   /**
    * Toggle "isItemDependency" in the right table
    */
   const handleToggleItemDependency = (itemId: number) => {
      setAddedDependencies((prev) =>
         prev.map((dep) =>
            dep.itemId === itemId
               ? { ...dep, isItemDependency: !dep.isItemDependency }
               : dep
         )
      );
   };

   /**
    * Remove an item from the right table
    */
   const handleRemoveItem = (itemId: number) => {
      setAddedDependencies((prev) =>
         prev.filter((dep) => dep.itemId !== itemId)
      );
   };

   /**
    * On Save => Map `addedDependencies` to your final shape & call your store/service
    */
   const handleSave = async () => {
      // Ensure all necessary selections are made
      if (
         !selectedProductId ||
         !selectedStageId ||
         !selectedOptionId ||
         !selectedItemId
      )
         return;

      // Build arrays to pass to updateDependencies
      const finalDeps = addedDependencies.map((dep) => ({
         dependentItemId: selectedItemId,
         affectingItemId: dep.itemId,
         isItemDependency: dep.isItemDependency,
         priceAdjustment: dep.priceAdjustment,
         affectingOptionId: dep.affectingOptionId,
         nameChange: dep.nameChange, // ✅ Include nameChange
         learnMoreId: dep.learnMoreId,
      }));

      // Call your store method
      await adminStore.updateDependencies(
         selectedProductId,
         selectedStageId,
         selectedOptionId,
         selectedItemId,
         finalDeps
      );

      setIsDialogOpen(false);
   };

   /**
    * Handle changing the LearnMore ID for a dependency
    */
   const handleChangeLearnMore = (itemId: number, learnMoreValue: string) => {
      const learnMoreId =
         learnMoreValue === "none" ? undefined : parseInt(learnMoreValue);
      setAddedDependencies((prev) =>
         prev.map((dep) =>
            dep.itemId === itemId ? { ...dep, learnMoreId } : dep
         )
      );
   };

   // ----- Derived Data -----

   const selectedOption = adminStore.allOptions.find(
      (opt) => opt.id === selectedSidebarOptionId
   );
   const itemsForSelectedOption = selectedOption?.items || [];

   // Filter by user search
   const filteredItems = itemsForSelectedOption.filter((it) =>
      it.name.toLowerCase().includes(itemSearch.toLowerCase())
   );

   return (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
         <DialogTrigger asChild>
            <Button onClick={handleOpenDialog}>Manage Dependencies</Button>
         </DialogTrigger>

         <DialogContent className="max-w-[95vw] md:max-w-[90vw] lg:max-w-[80vw] w-full max-h-[90vh] p-0 overflow-hidden flex flex-col">
            <DialogHeader className="px-4 md:px-6 pt-4 md:pt-6">
               <DialogTitle className="text-xl font-bold">
                  Manage Dependencies
               </DialogTitle>
               <DialogDescription>
                  Select an option and add its items to your dependencies list.
               </DialogDescription>
            </DialogHeader>

            {/* Main content area with responsive layout */}
            <div className="flex-1 overflow-hidden p-4 md:p-6">
               {/* Desktop view: 3-column layout */}
               <div className="hidden md:grid md:grid-cols-5 lg:grid-cols-7 gap-4 h-full">
                  {/* LEFT COLUMN: Options */}
                  <div className="md:col-span-1 lg:col-span-2 border rounded-lg p-3 bg-white shadow-sm h-[600px] overflow-hidden flex flex-col">
                     <h2 className="text-base font-semibold text-gray-800 mb-2">
                        Select Option
                     </h2>
                     <ScrollArea className="flex-1">
                        {/* Option list */}
                        <div className="space-y-1">
                           {adminStore.allOptions.map((option) => (
                              <button
                                 key={option.id}
                                 className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                    selectedSidebarOptionId === option.id
                                       ? "bg-primary text-primary-foreground font-medium"
                                       : "hover:bg-gray-100"
                                 }`}
                                 onClick={() =>
                                    setSelectedSidebarOptionId(option.id)
                                 }
                              >
                                 {option.name}
                              </button>
                           ))}
                        </div>
                     </ScrollArea>
                  </div>

                  {/* MIDDLE COLUMN: Items for Selected Option */}
                  <div className="md:col-span-2 border rounded-lg p-3 bg-white shadow-sm h-[600px] flex flex-col">
                     <div className="flex items-center justify-between mb-2">
                        <h2 className="text-base font-semibold text-gray-800">
                           {selectedOption?.name || "Items"}
                        </h2>
                        {selectedOption && (
                           <div className="relative">
                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                 type="text"
                                 placeholder="Search items..."
                                 className="pl-8 h-8 text-sm"
                                 value={itemSearch}
                                 onChange={(e) => setItemSearch(e.target.value)}
                              />
                           </div>
                        )}
                     </div>

                     <div className="flex-1 overflow-hidden">
                        {selectedOption ? (
                           filteredItems.length > 0 ? (
                              <ScrollArea className="h-full">
                                 <Table>
                                    <TableHeader>
                                       <TableRow>
                                          <TableHead>Item</TableHead>
                                          <TableHead className="w-20 text-right">
                                             Add
                                          </TableHead>
                                       </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                       {filteredItems.map((it) => (
                                          <TableRow key={it.id}>
                                             <TableCell className="font-medium">
                                                {it.name}
                                             </TableCell>
                                             <TableCell className="text-right">
                                                <Button
                                                   variant="outline"
                                                   size="sm"
                                                   className="h-8 w-8 p-0"
                                                   onClick={() =>
                                                      handleAddItem(
                                                         it.id,
                                                         it.name,
                                                         selectedOption?.id
                                                      )
                                                   }
                                                   disabled={addedDependencies.some(
                                                      (dep) =>
                                                         dep.itemId === it.id
                                                   )}
                                                >
                                                   <Plus className="h-4 w-4" />
                                                   <span className="sr-only">
                                                      Add
                                                   </span>
                                                </Button>
                                             </TableCell>
                                          </TableRow>
                                       ))}
                                    </TableBody>
                                 </Table>
                              </ScrollArea>
                           ) : (
                              <div className="flex items-center justify-center h-full">
                                 <p className="text-gray-500 text-sm">
                                    No items found matching "{itemSearch}"
                                 </p>
                              </div>
                           )
                        ) : (
                           <div className="flex items-center justify-center h-full">
                              <p className="text-gray-500 text-sm">
                                 Select an option to view its items.
                              </p>
                           </div>
                        )}
                     </div>
                  </div>

                  {/* RIGHT COLUMN: Selected Dependencies */}
                  <div className="md:col-span-2 lg:col-span-3 border rounded-lg p-3 bg-white shadow-sm h-[600px] flex flex-col">
                     <div className="flex items-center justify-between mb-2">
                        <h2 className="text-base font-semibold text-gray-800">
                           Selected Dependencies
                        </h2>
                        {addedDependencies.length > 0 && (
                           <span className="text-sm text-gray-500">
                              {addedDependencies.length} item
                              {addedDependencies.length > 1 ? "s" : ""}
                           </span>
                        )}
                     </div>

                     <div className="flex-1 overflow-hidden">
                        {addedDependencies.length === 0 ? (
                           <div className="flex items-center justify-center h-full">
                              <p className="text-gray-500 text-sm">
                                 No items added yet. Pick an option and click
                                 "Add".
                              </p>
                           </div>
                        ) : (
                           <Tabs
                              defaultValue="basic"
                              className="flex flex-col flex-1 min-h-0 h-full pb-20"
                           >
                              <TabsList className="grid grid-cols-2 bg-white shadow-sm">
                                 <TabsTrigger
                                    value="basic"
                                    className="py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 radix-state-active:bg-white radix-state-active:font-semibold"
                                 >
                                    Basic
                                 </TabsTrigger>
                                 <TabsTrigger
                                    value="advanced"
                                    className="py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 radix-state-active:bg-white radix-state-active:font-semibold"
                                 >
                                    Advanced
                                 </TabsTrigger>
                              </TabsList>

                              {/* BASIC Tab – scrollable */}
                              <TabsContent
                                 value="basic"
                                 className="flex-1 min-h-0 overflow-auto bg-gray-50"
                              >
                                 <ul className="divide-y divide-gray-200">
                                    {addedDependencies.map((dep) => (
                                       <li
                                          key={dep.itemId}
                                          className="flex flex-col px-4 py-3 hover:bg-white hover:shadow-sm transition"
                                       >
                                          <div className="flex justify-between items-center">
                                             <span className="text-md font-medium text-gray-800">
                                                {dep.itemName}
                                             </span>
                                             <Button
                                                variant="ghost"
                                                size="sm"
                                                className="p-1 text-destructive hover:bg-red-50"
                                                onClick={() =>
                                                   handleRemoveItem(dep.itemId)
                                                }
                                             >
                                                <Trash2 className="h-5 w-5" />
                                             </Button>
                                          </div>
                                          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                             <div className="flex flex-col">
                                                <label className="text-xs text-gray-500 mb-1">
                                                   Price Adjustment
                                                </label>
                                                <Input
                                                   type="number"
                                                   className="w-full"
                                                   value={dep.priceAdjustment}
                                                   onChange={(e) =>
                                                      handleChangePrice(
                                                         dep.itemId,
                                                         +e.target.value
                                                      )
                                                   }
                                                />
                                             </div>
                                             <div className="flex items-start">
                                                <input
                                                   id={`required-${dep.itemId}`}
                                                   type="checkbox"
                                                   className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                   checked={
                                                      dep.isItemDependency
                                                   }
                                                   onChange={() =>
                                                      handleToggleItemDependency(
                                                         dep.itemId
                                                      )
                                                   }
                                                />
                                                <label
                                                   htmlFor={`required-${dep.itemId}`}
                                                   className="ml-2 text-sm text-gray-700"
                                                >
                                                   Required
                                                </label>
                                             </div>
                                          </div>
                                       </li>
                                    ))}
                                 </ul>
                              </TabsContent>

                              {/* ADVANCED Tab – scrollable */}
                              <TabsContent
                                 value="advanced"
                                 className="flex-1 min-h-0 overflow-auto bg-gray-50"
                              >
                                 <ul className="divide-y divide-gray-200">
                                    {addedDependencies.map((dep) => (
                                       <li
                                          key={dep.itemId}
                                          className="flex flex-col px-4 py-3 hover:bg-white hover:shadow-sm transition"
                                       >
                                          <span className="text-md font-medium text-gray-800 mb-2">
                                             {dep.itemName}
                                          </span>
                                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                             <div className="flex flex-col">
                                                <label className="text-xs text-gray-500 mb-1">
                                                   Name Change
                                                </label>
                                                <Input
                                                   type="text"
                                                   className="w-full"
                                                   value={dep.nameChange || ""}
                                                   placeholder="Custom name…"
                                                   onChange={(e) =>
                                                      handleChangeName(
                                                         dep.itemId,
                                                         e.target.value
                                                      )
                                                   }
                                                />
                                             </div>
                                             <div className="flex flex-col">
                                                <label className="text-xs text-gray-500 mb-1">
                                                   Learn More Content
                                                </label>
                                                <Select
                                                   value={
                                                      dep.learnMoreId?.toString() ||
                                                      "none"
                                                   }
                                                   onValueChange={(value) =>
                                                      handleChangeLearnMore(
                                                         dep.itemId,
                                                         value
                                                      )
                                                   }
                                                >
                                                   <SelectTrigger className="w-full">
                                                      <SelectValue placeholder="Select content…" />
                                                   </SelectTrigger>
                                                   <SelectContent>
                                                      <SelectItem value="none">
                                                         None
                                                      </SelectItem>
                                                      {availableLearnMoreItems.map(
                                                         (item) => (
                                                            <SelectItem
                                                               key={item.id}
                                                               value={item.id.toString()}
                                                            >
                                                               {item.content
                                                                  .length > 30
                                                                  ? item.content.substring(
                                                                       0,
                                                                       30
                                                                    ) + "…"
                                                                  : item.content}
                                                            </SelectItem>
                                                         )
                                                      )}
                                                   </SelectContent>
                                                </Select>
                                             </div>
                                          </div>
                                       </li>
                                    ))}
                                 </ul>
                              </TabsContent>
                           </Tabs>
                        )}
                     </div>
                  </div>
               </div>

               {/* Mobile view: Tabbed interface */}
               <div className="md:hidden h-full">
                  <Tabs defaultValue="options" className="w-full h-full">
                     <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="options">Options</TabsTrigger>
                        <TabsTrigger value="items">Items</TabsTrigger>
                        <TabsTrigger value="dependencies">
                           Dependencies ({addedDependencies.length})
                        </TabsTrigger>
                     </TabsList>

                     {/* Options tab for mobile */}
                     <TabsContent
                        value="options"
                        className="h-[500px] overflow-auto border rounded-lg mt-2"
                     >
                        <div className="p-3">
                           <h3 className="text-sm font-medium mb-2">
                              Select an option
                           </h3>
                           <div className="space-y-1">
                              {adminStore.allOptions.map((option) => (
                                 <button
                                    key={option.id}
                                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                       selectedSidebarOptionId === option.id
                                          ? "bg-primary text-primary-foreground font-medium"
                                          : "hover:bg-gray-100"
                                    }`}
                                    onClick={() => {
                                       if (option.id) {
                                          setSelectedSidebarOptionId(option.id);
                                          // Auto-switch to items tab on mobile
                                          const itemsTab =
                                             document.querySelector(
                                                '[data-value="items"]'
                                             );
                                          if (itemsTab instanceof HTMLElement) {
                                             itemsTab.click();
                                          }
                                       }
                                    }}
                                 >
                                    {option.name}
                                 </button>
                              ))}
                           </div>
                        </div>
                     </TabsContent>

                     {/* Items tab for mobile */}
                     <TabsContent
                        value="items"
                        className="h-[500px] border rounded-lg mt-2"
                     >
                        <div className="p-3 h-full flex flex-col">
                           <div className="flex items-center justify-between mb-2">
                              <h3 className="text-sm font-medium">
                                 {selectedOption?.name ||
                                    "Select an option first"}
                              </h3>
                              {selectedOption && (
                                 <div className="relative w-full max-w-[200px]">
                                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                       type="text"
                                       placeholder="Search..."
                                       className="pl-8 h-8 text-sm"
                                       value={itemSearch}
                                       onChange={(e) =>
                                          setItemSearch(e.target.value)
                                       }
                                    />
                                 </div>
                              )}
                           </div>

                           <div className="flex-1 overflow-auto">
                              {selectedOption ? (
                                 filteredItems.length > 0 ? (
                                    <Table>
                                       <TableBody>
                                          {filteredItems.map((it) => (
                                             <TableRow key={it.id}>
                                                <TableCell>{it.name}</TableCell>
                                                <TableCell className="text-right">
                                                   <Button
                                                      variant="outline"
                                                      size="sm"
                                                      onClick={() => {
                                                         if (
                                                            it.id &&
                                                            selectedOption?.id
                                                         ) {
                                                            handleAddItem(
                                                               it.id,
                                                               it.name,
                                                               selectedOption.id
                                                            );
                                                            // Auto-switch to dependencies tab on mobile
                                                            const depsTab =
                                                               document.querySelector(
                                                                  '[data-value="dependencies"]'
                                                               );
                                                            if (
                                                               depsTab instanceof
                                                               HTMLElement
                                                            ) {
                                                               depsTab.click();
                                                            }
                                                         }
                                                      }}
                                                      disabled={addedDependencies.some(
                                                         (dep) =>
                                                            dep.itemId === it.id
                                                      )}
                                                   >
                                                      Add
                                                   </Button>
                                                </TableCell>
                                             </TableRow>
                                          ))}
                                       </TableBody>
                                    </Table>
                                 ) : (
                                    <div className="flex items-center justify-center h-full">
                                       <p className="text-gray-500 text-sm">
                                          No items found
                                       </p>
                                    </div>
                                 )
                              ) : (
                                 <div className="flex items-center justify-center h-full">
                                    <p className="text-gray-500 text-sm">
                                       Select an option first
                                    </p>
                                 </div>
                              )}
                           </div>
                        </div>
                     </TabsContent>

                     {/* Dependencies tab for mobile */}
                     <TabsContent
                        value="dependencies"
                        className="h-[500px] border rounded-lg mt-2"
                     >
                        <div className="p-3 h-full flex flex-col">
                           <h3 className="text-sm font-medium mb-2">
                              Selected Dependencies
                           </h3>

                           {addedDependencies.length === 0 ? (
                              <div className="flex items-center justify-center h-full">
                                 <p className="text-gray-500 text-sm">
                                    No items added yet
                                 </p>
                              </div>
                           ) : (
                              <div className="flex-1 overflow-auto">
                                 <Tabs defaultValue="basic" className="w-full">
                                    <TabsList className="w-full grid grid-cols-2">
                                       <TabsTrigger value="basic">
                                          Basic
                                       </TabsTrigger>
                                       <TabsTrigger value="advanced">
                                          Advanced
                                       </TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="basic">
                                       {addedDependencies.map((dep) => (
                                          <div
                                             key={dep.itemId}
                                             className="border-b py-3"
                                          >
                                             <div className="flex justify-between items-center mb-2">
                                                <div className="font-medium">
                                                   {dep.itemName}
                                                </div>
                                                <Button
                                                   variant="ghost"
                                                   size="sm"
                                                   className="h-8 w-8 p-0 text-destructive"
                                                   onClick={() =>
                                                      handleRemoveItem(
                                                         dep.itemId
                                                      )
                                                   }
                                                >
                                                   <Trash2 className="h-4 w-4" />
                                                </Button>
                                             </div>

                                             <div className="space-y-2">
                                                <div>
                                                   <label className="text-xs text-gray-500 block mb-1">
                                                      Price Adjustment
                                                   </label>
                                                   <Input
                                                      type="number"
                                                      className="w-full"
                                                      value={
                                                         dep.priceAdjustment
                                                      }
                                                      onChange={(e) =>
                                                         handleChangePrice(
                                                            dep.itemId,
                                                            +e.target.value
                                                         )
                                                      }
                                                   />
                                                </div>

                                                <div>
                                                   <label className="flex items-center">
                                                      <input
                                                         type="checkbox"
                                                         className="h-4 w-4 text-primary border-gray-300 rounded"
                                                         checked={
                                                            dep.isItemDependency
                                                         }
                                                         onChange={() =>
                                                            handleToggleItemDependency(
                                                               dep.itemId
                                                            )
                                                         }
                                                      />
                                                      <span className="ml-2 text-sm">
                                                         Required
                                                      </span>
                                                   </label>
                                                </div>
                                             </div>
                                          </div>
                                       ))}
                                    </TabsContent>

                                    <TabsContent value="advanced">
                                       {addedDependencies.map((dep) => (
                                          <div
                                             key={dep.itemId}
                                             className="border-b py-3"
                                          >
                                             <div className="font-medium mb-2">
                                                {dep.itemName}
                                             </div>

                                             <div className="space-y-3">
                                                <div>
                                                   <label className="text-xs text-gray-500 block mb-1">
                                                      Name Change
                                                   </label>
                                                   <Input
                                                      type="text"
                                                      className="w-full"
                                                      value={
                                                         dep.nameChange || ""
                                                      }
                                                      placeholder="Custom name..."
                                                      onChange={(e) =>
                                                         handleChangeName(
                                                            dep.itemId,
                                                            e.target.value
                                                         )
                                                      }
                                                   />
                                                </div>

                                                <div>
                                                   <label className="text-xs text-gray-500 block mb-1">
                                                      Learn More Content
                                                   </label>
                                                   <Select
                                                      value={
                                                         dep.learnMoreId?.toString() ||
                                                         "none"
                                                      }
                                                      onValueChange={(value) =>
                                                         handleChangeLearnMore(
                                                            dep.itemId,
                                                            value
                                                         )
                                                      }
                                                   >
                                                      <SelectTrigger className="w-full">
                                                         <SelectValue placeholder="Select content..." />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                         <SelectItem value="none">
                                                            None
                                                         </SelectItem>
                                                         {availableLearnMoreItems.map(
                                                            (item) => (
                                                               <SelectItem
                                                                  key={item.id}
                                                                  value={item.id.toString()}
                                                               >
                                                                  {item.content.substring(
                                                                     0,
                                                                     30
                                                                  )}
                                                                  {item.content
                                                                     .length >
                                                                  30
                                                                     ? "..."
                                                                     : ""}
                                                               </SelectItem>
                                                            )
                                                         )}
                                                      </SelectContent>
                                                   </Select>
                                                </div>
                                             </div>
                                          </div>
                                       ))}
                                    </TabsContent>
                                 </Tabs>
                              </div>
                           )}
                        </div>
                     </TabsContent>
                  </Tabs>
               </div>
            </div>

            {/* Footer */}
            <DialogFooter className="px-4 md:px-6 py-4 border-t">
               <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
               </Button>
               <Button onClick={handleSave}>Save Changes</Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
}
